import Button from "atoms/buttons/button";
import ChatPastConversationsPanel from "./chat-past-conversations-panel";
import ChatPrompt from "organisms/modals/chat-modal/chat-prompt";
import ChatResults from "organisms/modals/chat-modal/chat-results";
import Icon from "atoms/icons/icon";
import Modal, { CloseDialogEvent, ModalProps } from "molecules/modals/modal";
import React, { useCallback, useEffect, useRef, useState } from "react";
import StringUtils from "utilities/string-utils";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { AriaRole } from "utilities/enumerations/aria-role";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { CurrentConversationProvider } from "../../../utilities/atoms/conversations/current-conversation/current-conversation-provider";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { ModalConversationAtom } from "utilities/atoms/conversations/current-conversation/conversation-atoms";
import { ModalTransitions } from "molecules/constants/modal-transitions";
import { ModalTypes } from "molecules/constants/modal-types";
import { useChatModal } from "utilities/hooks/domain/chat/use-chat-modal";
import { useChromeBrowserDetection } from "utilities/hooks/use-chrome-browser-detection";
import { useConversationActions } from "utilities/atoms/conversations/current-conversation/use-conversation-actions.hook";
import { useConversationHistoryActions } from "utilities/atoms/conversations/conversation-history/use-conversation-history-actions.hook";
import { useLocalization } from "utilities/hooks/use-localization";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ChatModalProps
    extends Pick<ModalProps, "closeDialog" | "isVisible" | "onCloseComplete"> {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS_NAME = "c-chat-modal";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatModalContent: React.FunctionComponent<ChatModalProps> = (props) => {
    useChromeBrowserDetection();
    const { t } = useLocalization();
    const { closeDialog, isVisible, onCloseComplete } = props;
    const { isOnline } = useNetworkInformation();
    const { refreshConversationHistory } = useConversationHistoryActions();
    const { startConversation, sendPrompt } = useConversationActions();
    const [prompt, setPrompt] = useState("");
    const [showSearch, setShowSearch] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const { startingPrompt, setStartingPrompt } = useChatModal();

    const handleClose = useCallback(
        (event?: CloseDialogEvent) => closeDialog(event),
        [closeDialog]
    );

    const transition =
        window.innerWidth < Breakpoints.Phone
            ? ModalTransitions.SlideUp
            : ModalTransitions.SlideRight;
    const offlineClassName = !isOnline ? "-offline" : "";

    const initialPanelState = React.useMemo(() => {
        return window.innerWidth >= Breakpoints.Phone;
    }, []);

    const [newChatStarted, setNewChatStarted] = React.useState<boolean>(false);
    const [pastConversationPanelOpen, setPastConversationPanelOpen] =
        React.useState<boolean>(initialPanelState);
    const [searchText, setSearchText] = useState<string>("");

    const handlePanelState = () => {
        setPastConversationPanelOpen((prev) => !prev);
        if (pastConversationPanelOpen) {
            refreshConversationHistory();
        }
        setShowSearch(false);
        setSearchText("");
    };

    useEffect(() => {
        if (props.isVisible) {
            // Refresh conversation history when the modal is opened
            refreshConversationHistory();
        }
    }, [props.isVisible, refreshConversationHistory]);

    useEffect(() => {
        if (StringUtils.hasValue(startingPrompt) && isVisible) {
            startConversation();
            sendPrompt(startingPrompt);
            setStartingPrompt(undefined);
        }
    }, [
        startConversation,
        setStartingPrompt,
        startingPrompt,
        sendPrompt,
        isVisible,
    ]);

    return (
        <Modal
            closeDialog={handleClose}
            isVisible={isVisible}
            label={t("nfpa-ai-chat")}
            onCloseComplete={onCloseComplete}
            showAppSidebar={true}
            transition={transition}
            closeButtonStyle={ModalCloseButtonStyle.InsideDialog}
            type={ModalTypes.Chat}>
            <div className={`${BASE_CLASS_NAME}__header`}>
                {!pastConversationPanelOpen && (
                    <Button
                        accessibleText={t("menu")}
                        style={ButtonStyles.Tertiary}
                        cssClassName={`${BASE_CLASS_NAME}__menu`}
                        onClick={handlePanelState}
                        size={ButtonSizes.Small}>
                        <Icon type={Icons.Menu2} size={IconSizes.Medium} />
                    </Button>
                )}
            </div>
            <div className={`${BASE_CLASS_NAME} ${offlineClassName}`}>
                <ChatPastConversationsPanel
                    closePanel={handlePanelState}
                    handleNewChatStarted={setNewChatStarted}
                    handleSearchText={setSearchText}
                    handleShowSearch={setShowSearch}
                    isOpen={pastConversationPanelOpen}
                    searchText={searchText}
                    showSearch={showSearch}
                />
                <div
                    className={`${BASE_CLASS_NAME}__content${pastConversationPanelOpen ? " -panel-open" : ""}`}
                    role={AriaRole.TabPanel}>
                    <div
                        className={`${BASE_CLASS_NAME}__content__results`}
                        ref={containerRef}>
                        <ChatResults
                            onCitationClick={handleClose}
                            includeConversationId={true}
                            containerRef={containerRef}
                            isBookview={false}
                        />
                    </div>
                    <div className={`${BASE_CLASS_NAME}__content__prompt`}>
                        <ChatPrompt
                            newChatStarted={newChatStarted}
                            prompt={prompt}
                            handleSetPrompt={setPrompt}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Export
// -----------------------------------------------------------------------------------------

const ChatModal: React.FC<ChatModalProps> = (props) => {
    return (
        <CurrentConversationProvider atom={ModalConversationAtom}>
            <ChatModalContent {...props} />
        </CurrentConversationProvider>
    );
};

export default ChatModal;

// #endregion Export
